import { useMemo, useSyncExternalStore } from 'react';

type NavigateOptions = {
  replace?: boolean;
};

type Href = string;

const LOCATION_CHANGE_EVENT = 'app-location-change';

const normalizePathname = (pathname: string) => {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
};

const getLocationSnapshot = () => {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const subscribeLocation = (onStoreChange: () => void) => {
  if (typeof window === 'undefined') return () => undefined;

  window.addEventListener('popstate', onStoreChange);
  window.addEventListener(LOCATION_CHANGE_EVENT, onStoreChange);

  return () => {
    window.removeEventListener('popstate', onStoreChange);
    window.removeEventListener(LOCATION_CHANGE_EVENT, onStoreChange);
  };
};

const notifyLocationChange = () => {
  window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
  window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
};

const navigate = (href: Href, options: NavigateOptions = {}) => {
  if (typeof window === 'undefined') return;

  if (options.replace) {
    window.history.replaceState(null, '', href);
  } else {
    window.history.pushState(null, '', href);
  }

  notifyLocationChange();
};

function useLocationSnapshot() {
  return useSyncExternalStore(subscribeLocation, getLocationSnapshot, () => '/');
}

export function useAppPathname() {
  const snapshot = useLocationSnapshot();
  const pathname = snapshot.split(/[?#]/, 1)[0] || '/';
  return normalizePathname(pathname);
}

export function useAppSearchParams() {
  const snapshot = useLocationSnapshot();
  const queryString = snapshot.includes('?') ? snapshot.split('?')[1]?.split('#')[0] || '' : '';
  return useMemo(() => new URLSearchParams(queryString), [queryString]);
}

export function useAppRouter() {
  return useMemo(
    () => ({
      push: (href: Href) => navigate(href),
      replace: (href: Href) => navigate(href, { replace: true }),
      back: () => window.history.back(),
      forward: () => window.history.forward(),
      refresh: () => window.location.reload()
    }),
    []
  );
}

interface AppLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: Href;
}

export function AppLink({ href, onClick, ...props }: AppLinkProps) {
  return (
    <a
      href={href}
      onClick={event => {
        onClick?.(event);

        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.altKey ||
          event.ctrlKey ||
          event.shiftKey ||
          props.target
        ) {
          return;
        }

        event.preventDefault();
        navigate(href);
      }}
      {...props}
    />
  );
}

export function navigateTo(href: string) {
  navigate(href);
}

export function replaceTo(href: string) {
  navigate(href, { replace: true });
}

export const useRouter = useAppRouter;
export const usePathname = useAppPathname;
export const useSearchParams = useAppSearchParams;

export function notFound(): never {
  throw new Error('Route not found');
}
